import { download, generateCsv, mkConfig } from "export-to-csv"
import { DateTime } from "luxon"
import { useCallback } from "react"
import { useDateTime } from "../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { CancellationRateEntry, OccupancyRateEntry } from "../statisticsTypes"

type useExportRateEntryToCsvParams = Readonly<{
    data: OccupancyRateEntry[] | CancellationRateEntry[] | null | undefined
    startDate: DateTime
    endDate: DateTime
    isOccupancyRate?: boolean
}>

export function useExportRateEntry({
    data,
    startDate,
    endDate,
    isOccupancyRate = false,
}: useExportRateEntryToCsvParams) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-${isOccupancyRate ? "occupancy" : "cancellation"}-occupancy-${dateTimeFactory.formatDate(
            startDate,
        )}_${dateTimeFactory.formatDate(endDate)}`,
    })

    const mapEntryToCsvRow = (entry: OccupancyRateEntry | CancellationRateEntry) => {
        const rateFieldname = isOccupancyRate ? "Occupancy Rate" : "Cancellation Rate"

        return {
            From: dateTimeFactory.formatDate(startDate),
            To: dateTimeFactory.formatDate(endDate),
            Resource: entry.resource.displayName,
            [rateFieldname]:
                "occupancyRate" in entry
                    ? entry.occupancyRate
                    : "cancellationRate" in entry
                    ? entry.cancellationRate
                    : null,
            Status: entry.status.label,
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapEntryToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((entry) => mapEntryToCsvRow(entry))
        } else {
            exportRows = [mapEntryToCsvRow({} as OccupancyRateEntry)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, data, startDate, endDate, l10n])
}
